import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SlServicesService } from 'src/app/shared/sl-services/sl-services.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { KnowledgeDocumentModalComponent } from '../knowledge-document-modal/knowledge-document-modal.component';

export interface KnowledgeDocument {
    id: string;
    title: string;
    content: string;
    created_at?: string;
    updated_at?: string;
}

@Component({
    selector: 'app-ai-settings',
    templateUrl: './ai-settings.component.html',
    styleUrls: ['./ai-settings.component.scss']
})
export class AiSettingsComponent implements OnInit {
    @Input() actionInProgress$: BehaviorSubject<boolean>;

    documents$ = new BehaviorSubject<KnowledgeDocument[]>([]);
    isLoading$ = new BehaviorSubject<boolean>(false);
    totalDocuments = 0;

    constructor(
        private services: SlServicesService,
        private utilities: UtilitiesService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef
    ) {}

    async ngOnInit(): Promise<void> {
        await this.loadDocuments();
    }

    async loadDocuments(): Promise<void> {
        try {
            this.isLoading$.next(true);
            const result = await this.services.callService('listKnowledgeDocuments', {
                limit: 100,
                offset: 0
            });

            if (result.success) {
                this.documents$.next(result.documents);
                this.totalDocuments = result.total;
            }
        } catch (error) {
            console.error('Error loading knowledge documents:', error);
            this.utilities.notifyError('Failed to load knowledge documents');
        } finally {
            this.isLoading$.next(false);
        }
    }

    openCreateModal(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.width = '600px';
        dialogConfig.data = { document: null };

        const dialogRef = this.dialog.open(KnowledgeDocumentModalComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result?.saved) {
                await this.loadDocuments();
            }
        });
    }

    openEditModal(document: KnowledgeDocument): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.width = '600px';
        dialogConfig.data = { document };

        const dialogRef = this.dialog.open(KnowledgeDocumentModalComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result?.saved) {
                await this.loadDocuments();
            }
        });
    }

    async deleteDocument(document: KnowledgeDocument): Promise<void> {
        if (!confirm(`Are you sure you want to delete "${document.title}"?`)) {
            return;
        }

        try {
            this.actionInProgress$.next(true);
            const result = await this.services.callService('deleteKnowledgeDocument', {
                id: document.id
            });

            if (result.success) {
                this.utilities.notifySuccess('Knowledge document deleted successfully');
                await this.loadDocuments();
            }
        } catch (error) {
            console.error('Error deleting knowledge document:', error);
            this.utilities.notifyError('Failed to delete knowledge document');
        } finally {
            this.actionInProgress$.next(false);
        }
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    truncateContent(content: string, maxLength: number = 100): string {
        if (!content) return '';
        if (content.length <= maxLength) return content;
        return content.substring(0, maxLength) + '...';
    }
}
